import { api, type Page } from './client'

/**
 * TASK-0098: mirrors `FailedOutboxRow` in `OutboxController.cs` exactly — field names and casing
 * as returned by System.Text.Json's default camelCase policy. No `entityId`: `OutboxMessage` has
 * no such concept (see the controller's remarks on `ListFailed`), and no `payload` — the list
 * endpoint deliberately omits it, see `FailedOutboxRowDetail` below.
 */
export interface FailedOutboxRow {
  id: number
  eventType: string
  attempts: number
  lastError: string | null
  occurredAt: string
  createdAt: string
}

/**
 * Mirrors `FailedOutboxRowDetail` in `OutboxController.cs`: the same fields as `FailedOutboxRow`
 * plus the raw `payload` (a JSON string, not a parsed object — the backend stores and returns it
 * as `jsonb`/`string`, it does not attempt to interpret it).
 */
export interface FailedOutboxRowDetail extends FailedOutboxRow {
  payload: string
}

export const outboxApi = {
  /** `GET /api/outbox/failed?page=&size=` — Admin-only. */
  listFailed: (page = 1, size = 25) =>
    api<Page<FailedOutboxRow>>(`/api/outbox/failed?page=${page}&size=${size}`),

  /** `GET /api/outbox/failed/{id}` — 404 if the row does not exist or is no longer Failed. */
  getFailed: (id: number) =>
    api<FailedOutboxRowDetail>(`/api/outbox/failed/${id}`),

  /**
   * `POST /api/outbox/failed/{id}/replay` — 204 on success, 404 if the row does not exist or is
   * no longer Failed, 409 if it changed between read and write (see the controller's remarks on
   * `Replay`). Returns `void` on success (the `api()` client short-circuits 204 to `undefined`).
   */
  replay: (id: number) =>
    api<void>(`/api/outbox/failed/${id}/replay`, { method: 'POST' }),
}
